import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/react-hoc';
import { FormattedMessage, defineMessages } from 'react-intl';
import Icon from '../../components/Icon';
import Button from '../../components/Button';
import Header from '../../components/Header';
import Modal from '../../components/Modal';
import Loader from '../../components/Loader';
import Dimmer from '../../components/Dimmer';
import Message from '../../components/Message';
import updateHEUserMutation from '../../mutations/HEAccount/updateHEUser';
import { SimpleGraphQLComponent } from '../../utils/apollo';

const messages = defineMessages({
  error: {
    id: 'primary-user.re-invite-user.error',
    defaultMessage: 'Unable to re-invite user',
  },
  reInviteUser: {
    id: 'primary-user.re-invite-user.header',
    defaultMessage: 'Re-invite User',
  },
  reInviteUserText: {
    id: 'primary-user.re-invite-user.text',
    defaultMessage: 'Would you like to re-invite {userName}?',
  },
});

export class ReInviteUserComponent extends React.PureComponent {
  static propTypes = {
    heAccount: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    user: PropTypes.shape({
      id: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
    }).isRequired,
    mutateUpdateHEUser: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
  };

  state = {
    isError: false,
    requesting: false,
  };

  _handleClose = () => {
    this.setState({ requesting: false });
    this.props.onClose();
  };

  _handleSave = () => {
    const { user, heAccount } = this.props;

    this.setState({ isError: false, requesting: true });
    this.props.mutateUpdateHEUser({
      variables: {
        input: {
          heAccount: {
            id: heAccount.id,
            user: {
              id: user.id,
              recreate: true,
            },
          },
        },
      },
    }).then(this._handleClose, () => {
      this.setState({ isError: true, requesting: false });
    });
  };

  render() {
    const { user } = this.props;
    const { isError, requesting } = this.state;
    const userName = `${user.firstName} ${user.lastName}`;
    const errorDetails = isError ? (
      <Message role="alert" negative>
        <Message.Header><FormattedMessage {...messages.error} /></Message.Header>
      </Message>
    ) : '';

    return (
      <Dimmer.Dimmable as={Modal} open onClose={this._handleClose} data-cy="he-reInvite-user-close-button">
        <Dimmer
          tabIndex={-1}
          role="progressbar"
          aria-describedby="Re-inviting User..."
          aria-busy={requesting}
          inverted
          active={requesting}
        >
          <Loader active={requesting} />
        </Dimmer>
        <Header data-cy="he-reInvite-user-header-title">
          <FormattedMessage {...messages.reInviteUser} />
        </Header>
        <Modal.Content data-cy="he-reInvite-user-content-text">
          <p>
            <FormattedMessage
              {...messages.reInviteUserText}
              values={{
                userName,
              }}
            />
          </p>
          {errorDetails}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this._handleClose} data-cy="he-reInvite-user-no-button">
            <Icon name="remove" />
            <FormattedMessage id="common.button.no" defaultMessage="No" />
          </Button>
          <Button primary onClick={this._handleSave} data-cy="he-reInvite-user-yes-button">
            <Icon name="checkmark" />
            <FormattedMessage id="common.button.yes" defaultMessage="Yes" />
          </Button>
        </Modal.Actions>
      </Dimmer.Dimmable>
    );
  }
}

const updateHEUser = graphql(updateHEUserMutation, {
  name: 'mutateUpdateHEUser',
});

export default SimpleGraphQLComponent(updateHEUser)(ReInviteUserComponent);
